import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import { useActiveOrg } from '../activeOrg';
import {
  PASSWORD_MIN_LENGTH,
  PASSWORD_MIN_SCORE,
  scorePassword
} from '../passwordScore';

export interface AccountProfile {
  id: string;
  display_name: string;
  username: string;
  /** 4-digit suffix; immutable across renames. */
  discriminator: number;
}

export type AccountStatus = 'loading' | 'ready' | 'saving' | 'unauth' | 'error';

/** Email-change form lifecycle. */
export type EmailChangeStatus = 'idle' | 'submitting' | 'sent' | 'error';

/** Password-change form lifecycle. */
export type PasswordChangeStatus = 'idle' | 'submitting' | 'success' | 'error';

/** Sessions sign-out lifecycle. */
export type SessionsStatus = 'idle' | 'submitting' | 'success' | 'error';

/** Delete-account form lifecycle. */
export type DeleteAccountStatus = 'idle' | 'submitting' | 'error';

const USERNAME_PATTERN = /^[A-Za-z0-9_-]{3,32}$/;
const EMAIL_PATTERN = /.+@.+\..+/;

function formatOrgList(names: string[]): string {
  if (names.length <= 1) return names[0] ?? '';
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`;
}

export interface SoleOwnerOrg {
  id: string;
  name: string;
}

export interface UseAccountVM {
  status: AccountStatus;
  email: string | null;
  profile: AccountProfile | null;
  /** Name of the user's active org, sourced from the shell's `useActiveOrg`.
   *  Null while the shell is still resolving the active org. */
  activeOrgName: string | null;
  /** Function-role label in the active org. Null when no role is assigned. */
  functionRoleLabel: string | null;
  /** Orgs where the calling user is the only Owner. Derived from the
   *  `sole_owner_org_ids()` RPC. Drives the Danger-zone gating (turn 1.6b):
   *  when non-empty, the Delete-account button is disabled and points the
   *  user at Members to transfer ownership first. */
  soleOwnerOrgs: SoleOwnerOrg[];
  /** Form-bound editable values (mirror profile on load; diverge on edit). */
  displayName: string;
  username: string;
  /** Trimmed `[A-Za-z0-9_-]{3,32}` validity for the in-form username. */
  usernameValid: boolean;
  /** Per-form error message after a save attempt (e.g. username regex fail). */
  profileError: string | null;
  /** Last save's success message; consumers clear after rendering it. */
  profileSavedAt: number | null;
  /** Has the form diverged from the loaded profile? */
  dirty: boolean;
  /** Email-change form open/closed. */
  emailFormOpen: boolean;
  /** Form-bound new-email value. */
  newEmail: string;
  /** Lifecycle for the most recent change request. */
  emailStatus: EmailChangeStatus;
  /** Inline error for the email field / server response. */
  emailError: string | null;
  /** Address the confirmation link was sent to, after a successful submit. */
  emailSentTo: string | null;
  /** Password-change form open/closed. */
  passwordFormOpen: boolean;
  /** Form-bound current password (used to re-verify the session before changing). */
  currentPassword: string;
  /** Form-bound new password value (drives the strength meter). */
  newPassword: string;
  /** Form-bound confirm-new-password value. */
  confirmPassword: string;
  /** Lifecycle for the most recent change-password attempt. */
  passwordStatus: PasswordChangeStatus;
  /** Inline error for the password form (wrong current, server error, etc.). */
  passwordError: string | null;
  /** Timestamp of the last successful password change — drives the toast. */
  passwordChangedAt: number | null;
  /** Lifecycle for the most recent sign-out-everywhere-else attempt. */
  sessionsStatus: SessionsStatus;
  /** Inline error surfaced near the Sessions section button (rate-limit etc.). */
  sessionsError: string | null;
  /** Timestamp of the last successful sign-out-everywhere-else — drives the toast. */
  sessionsSignedOutAt: number | null;
  /** Delete-account form open/closed. */
  deleteFormOpen: boolean;
  /** Form-bound handle confirmation value (must equal `@<username>#<NNNN>`). */
  deleteConfirmHandle: string;
  /** Lifecycle for the most recent delete-account attempt. */
  deleteStatus: DeleteAccountStatus;
  /** Inline error for the delete-account form (sole-Owner race, rate-limit, …). */
  deleteError: string | null;
  /** True when the typed handle matches `@<username>#<NNNN>` exactly. */
  deleteHandleMatches: boolean;
  actions: {
    setDisplayName: (v: string) => void;
    setUsername: (v: string) => void;
    saveProfile: () => Promise<void>;
    clearSavedToast: () => void;
    openEmailForm: () => void;
    cancelEmailForm: () => void;
    setNewEmail: (v: string) => void;
    requestEmailChange: () => Promise<void>;
    openPasswordForm: () => void;
    cancelPasswordForm: () => void;
    setCurrentPassword: (v: string) => void;
    setNewPassword: (v: string) => void;
    setConfirmPassword: (v: string) => void;
    changePassword: () => Promise<void>;
    clearPasswordToast: () => void;
    signOutOthers: () => Promise<void>;
    clearSessionsToast: () => void;
    openDeleteForm: () => void;
    cancelDeleteForm: () => void;
    setDeleteConfirmHandle: (v: string) => void;
    deleteAccount: () => Promise<void>;
  };
}

/**
 * ViewModel for `/account`. v1 covers the Profile section only (turn 1.1):
 * loads `profiles` row for the signed-in user, exposes editable display name +
 * username, persists via PATCH. Email + password + sessions + danger zone
 * land in subsequent turns.
 */
export function useAccountVM(): UseAccountVM {
  const [status, setStatus] = useState<AccountStatus>('loading');
  const [email, setEmail] = useState<string | null>(null);
  const [profile, setProfile] = useState<AccountProfile | null>(null);
  const [displayName, setDisplayName] = useState('');
  const [username, setUsername] = useState('');
  const [profileError, setProfileError] = useState<string | null>(null);
  const [profileSavedAt, setProfileSavedAt] = useState<number | null>(null);
  const [emailFormOpen, setEmailFormOpen] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [emailStatus, setEmailStatus] = useState<EmailChangeStatus>('idle');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [emailSentTo, setEmailSentTo] = useState<string | null>(null);
  const [passwordFormOpen, setPasswordFormOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordStatus, setPasswordStatus] = useState<PasswordChangeStatus>('idle');
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordChangedAt, setPasswordChangedAt] = useState<number | null>(null);
  // Active org is owned by the shell (`useActiveOrg`). This VM is a pure
  // consumer: it must NOT re-derive its own "oldest membership" org — that was
  // the cross-org leak. The function-role label is re-fetched scoped to it.
  const { activeOrgId, activeOrgName } = useActiveOrg();
  const [functionRoleLabel, setFunctionRoleLabel] = useState<string | null>(null);
  const [soleOwnerOrgs, setSoleOwnerOrgs] = useState<SoleOwnerOrg[]>([]);
  const [sessionsStatus, setSessionsStatus] = useState<SessionsStatus>('idle');
  const [sessionsError, setSessionsError] = useState<string | null>(null);
  const [sessionsSignedOutAt, setSessionsSignedOutAt] = useState<number | null>(null);
  const [deleteFormOpen, setDeleteFormOpen] = useState(false);
  const [deleteConfirmHandle, setDeleteConfirmHandle] = useState('');
  const [deleteStatus, setDeleteStatus] = useState<DeleteAccountStatus>('idle');
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data: sessionData } = await supabase.auth.getSession();
      if (cancelled) return;
      const session = sessionData.session;
      if (!session) {
        setStatus('unauth');
        return;
      }
      setEmail(session.user.email ?? null);
      // No active org yet — the shell is still resolving which org to land in.
      // Stay in the loading state; this effect re-runs once it resolves.
      if (!activeOrgId) return;
      const [
        { data: row, error },
        { data: membershipRow },
        { data: soleOwnerRows }
      ] = await Promise.all([
        supabase
          .from('profiles')
          .select('id, display_name, username, discriminator')
          .eq('id', session.user.id)
          .maybeSingle(),
        // Function-role label for THIS org's membership (the active org the
        // shell published), not the viewer's oldest membership.
        supabase
          .from('memberships')
          .select('function_role:function_role_id(label)')
          .eq('user_id', session.user.id)
          .eq('org_id', activeOrgId)
          .maybeSingle(),
        supabase.rpc('sole_owner_org_ids')
      ]);
      if (cancelled) return;
      if (error || !row) {
        setStatus('error');
        return;
      }
      const p: AccountProfile = {
        id: row.id,
        display_name: row.display_name ?? '',
        username: row.username ?? '',
        discriminator: Number(row.discriminator ?? 0)
      };
      setProfile(p);
      setDisplayName(p.display_name);
      setUsername(p.username);

      type RoleEmbed = { label?: string } | { label?: string }[] | null;
      const membership = membershipRow as { function_role: RoleEmbed } | null;
      const roleEmbed = membership?.function_role ?? null;
      const roleLabel = Array.isArray(roleEmbed)
        ? roleEmbed[0]?.label ?? null
        : roleEmbed?.label ?? null;
      setFunctionRoleLabel(roleLabel);
      const soleRows = (soleOwnerRows ?? []) as ReadonlyArray<{
        id?: string;
        name?: string;
      }>;
      setSoleOwnerOrgs(
        soleRows
          .filter((r): r is { id: string; name: string } => !!r.id && !!r.name)
          .map((r) => ({ id: r.id, name: r.name }))
      );
      setStatus('ready');
    })();
    return () => {
      cancelled = true;
    };
  }, [activeOrgId]);

  const trimmedUsername = username.trim();
  const usernameValid = USERNAME_PATTERN.test(trimmedUsername);
  const dirty = useMemo(() => {
    if (!profile) return false;
    return (
      displayName !== profile.display_name || trimmedUsername !== profile.username
    );
  }, [profile, displayName, trimmedUsername]);

  const saveProfile = useCallback(async () => {
    if (!profile) return;
    setProfileError(null);
    if (!usernameValid) {
      setProfileError(
        'Username must be 3–32 chars, letters/digits/underscore/dash.'
      );
      return;
    }
    setStatus('saving');
    const { error } = await supabase
      .from('profiles')
      .update({ display_name: displayName, username: trimmedUsername })
      .eq('id', profile.id);
    if (error) {
      setProfileError("Couldn't save your profile. Please try again.");
      setStatus('ready');
      return;
    }
    setProfile({
      ...profile,
      display_name: displayName,
      username: trimmedUsername
    });
    setProfileSavedAt(Date.now());
    setStatus('ready');
  }, [profile, displayName, trimmedUsername, usernameValid]);

  const clearSavedToast = useCallback(() => setProfileSavedAt(null), []);

  const openEmailForm = useCallback(() => {
    setEmailFormOpen(true);
    setEmailError(null);
    setEmailSentTo(null);
    setEmailStatus('idle');
  }, []);

  const cancelEmailForm = useCallback(() => {
    setEmailFormOpen(false);
    setNewEmail('');
    setEmailError(null);
    setEmailSentTo(null);
    setEmailStatus('idle');
  }, []);

  const requestEmailChange = useCallback(async () => {
    const trimmed = newEmail.trim();
    setEmailError(null);
    if (!EMAIL_PATTERN.test(trimmed)) {
      setEmailError('Enter a valid email address.');
      return;
    }
    if (email && trimmed.toLowerCase() === email.toLowerCase()) {
      setEmailError('That is already your current email.');
      return;
    }
    setEmailStatus('submitting');
    const { error } = await supabase.auth.updateUser({ email: trimmed });
    if (error) {
      setEmailStatus('error');
      setEmailError(
        /rate|too many/i.test(error.message)
          ? 'Too many requests. Try again in a few minutes.'
          : "Couldn't request that change. Please try again."
      );
      return;
    }
    setEmailStatus('sent');
    setEmailSentTo(trimmed);
  }, [newEmail, email]);

  const openPasswordForm = useCallback(() => {
    setPasswordFormOpen(true);
    setPasswordError(null);
    setPasswordStatus('idle');
  }, []);

  const cancelPasswordForm = useCallback(() => {
    setPasswordFormOpen(false);
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setPasswordError(null);
    setPasswordStatus('idle');
  }, []);

  const clearPasswordToast = useCallback(() => setPasswordChangedAt(null), []);

  const changePassword = useCallback(async () => {
    if (!email) {
      setPasswordError('Sign in again before changing your password.');
      return;
    }
    setPasswordError(null);
    if (newPassword.length < PASSWORD_MIN_LENGTH) {
      setPasswordError(
        `New password must be at least ${PASSWORD_MIN_LENGTH} characters.`
      );
      return;
    }
    if (scorePassword(newPassword) < PASSWORD_MIN_SCORE) {
      setPasswordError('Choose a stronger password ("Fair" or better).');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError("Passwords don't match.");
      return;
    }
    if (newPassword === currentPassword) {
      setPasswordError('New password must be different from the current one.');
      return;
    }
    setPasswordStatus('submitting');
    const reauth = await supabase.auth.signInWithPassword({
      email,
      password: currentPassword
    });
    if (reauth.error) {
      const msg = reauth.error.message?.toLowerCase() ?? '';
      setPasswordStatus('error');
      setPasswordError(
        /rate|too many/.test(msg)
          ? 'Too many requests. Try again in a few minutes.'
          : 'Current password is incorrect.'
      );
      return;
    }
    const update = await supabase.auth.updateUser({ password: newPassword });
    if (update.error) {
      const msg = update.error.message?.toLowerCase() ?? '';
      setPasswordStatus('error');
      setPasswordError(
        /rate|too many/.test(msg)
          ? 'Too many requests. Try again in a few minutes.'
          : "Couldn't update your password. Please try again."
      );
      return;
    }
    setPasswordStatus('success');
    setPasswordChangedAt(Date.now());
    setPasswordFormOpen(false);
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
  }, [email, currentPassword, newPassword, confirmPassword]);

  const clearSessionsToast = useCallback(() => {
    setSessionsSignedOutAt(null);
    setSessionsStatus('idle');
  }, []);

  const expectedHandle = useMemo(
    () =>
      profile
        ? `@${profile.username}#${String(profile.discriminator).padStart(4, '0').slice(-4)}`
        : '',
    [profile]
  );
  const deleteHandleMatches =
    !!expectedHandle && deleteConfirmHandle === expectedHandle;

  const openDeleteForm = useCallback(() => {
    setDeleteFormOpen(true);
    setDeleteConfirmHandle('');
    setDeleteError(null);
    setDeleteStatus('idle');
  }, []);

  const cancelDeleteForm = useCallback(() => {
    setDeleteFormOpen(false);
    setDeleteConfirmHandle('');
    setDeleteError(null);
    setDeleteStatus('idle');
  }, []);

  const deleteAccount = useCallback(async () => {
    if (!deleteHandleMatches) return;
    setDeleteError(null);
    setDeleteStatus('submitting');
    const { error } = await supabase.rpc('delete_account');
    if (error) {
      const code = (error as { code?: string }).code;
      const msg = error.message?.toLowerCase() ?? '';
      const status = (error as { status?: number }).status;
      if (code === 'P0001' || /sole_owner/.test(msg)) {
        const detailsRaw =
          (error as { details?: string | null }).details ??
          (error as { detail?: string | null }).detail ??
          null;
        let names: string[] = [];
        let ids: string[] = [];
        if (detailsRaw) {
          try {
            const parsed = JSON.parse(detailsRaw) as {
              org_ids?: unknown;
              org_names?: unknown;
            };
            if (Array.isArray(parsed.org_names)) {
              names = parsed.org_names.filter(
                (n): n is string => typeof n === 'string'
              );
            }
            if (Array.isArray(parsed.org_ids)) {
              ids = parsed.org_ids.filter(
                (n): n is string => typeof n === 'string'
              );
            }
          } catch {
            /* ignore */
          }
        }
        if (names.length) {
          setSoleOwnerOrgs(
            names.map((name, i) => ({ id: ids[i] ?? name, name }))
          );
          setDeleteError(
            `You're the sole Owner of ${formatOrgList(names)}. Transfer ownership or delete the org before deleting your account.`
          );
        } else {
          setDeleteError(
            "You're the sole Owner of one or more orgs. Transfer ownership or delete the org before deleting your account."
          );
        }
        setDeleteStatus('error');
        return;
      }
      setDeleteStatus('error');
      setDeleteError(
        status === 429 || /rate|too many/.test(msg)
          ? 'Too many requests. Try again in a few minutes.'
          : "Couldn't delete your account. Please try again."
      );
      return;
    }
    await supabase.auth.signOut();
    navigate('/sign-in', { replace: true });
  }, [deleteHandleMatches, navigate]);

  const signOutOthers = useCallback(async () => {
    setSessionsError(null);
    setSessionsStatus('submitting');
    const { error } = await supabase.auth.signOut({ scope: 'others' });
    if (error) {
      const msg = error.message?.toLowerCase() ?? '';
      const status = (error as { status?: number }).status;
      setSessionsStatus('error');
      setSessionsError(
        status === 429 || /rate|too many/.test(msg)
          ? 'Too many requests. Try again in a few minutes.'
          : "Couldn't sign out other sessions. Please try again."
      );
      return;
    }
    setSessionsStatus('success');
    setSessionsSignedOutAt(Date.now());
  }, []);

  return {
    status,
    email,
    profile,
    activeOrgName,
    functionRoleLabel,
    soleOwnerOrgs,
    displayName,
    username,
    usernameValid,
    profileError,
    profileSavedAt,
    dirty,
    emailFormOpen,
    newEmail,
    emailStatus,
    emailError,
    emailSentTo,
    passwordFormOpen,
    currentPassword,
    newPassword,
    confirmPassword,
    passwordStatus,
    passwordError,
    passwordChangedAt,
    sessionsStatus,
    sessionsError,
    sessionsSignedOutAt,
    deleteFormOpen,
    deleteConfirmHandle,
    deleteStatus,
    deleteError,
    deleteHandleMatches,
    actions: {
      setDisplayName,
      setUsername,
      saveProfile,
      clearSavedToast,
      openEmailForm,
      cancelEmailForm,
      setNewEmail,
      requestEmailChange,
      openPasswordForm,
      cancelPasswordForm,
      setCurrentPassword,
      setNewPassword,
      setConfirmPassword,
      changePassword,
      clearPasswordToast,
      signOutOthers,
      clearSessionsToast,
      openDeleteForm,
      cancelDeleteForm,
      setDeleteConfirmHandle,
      deleteAccount
    }
  };
}
